import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Send, Box, Droplets, Scale, LogOut, ArrowRight, Clock, AlertCircle, CheckCircle, Plus, Trash2, Layers, MoveRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getItalianDateTime = () => {
  const now = new Date();
  const italianTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Rome' }));
  const year = italianTime.getFullYear();
  const month = String(italianTime.getMonth() + 1).padStart(2, '0');
  const day = String(italianTime.getDate()).padStart(2, '0');
  const hours = String(italianTime.getHours()).padStart(2, '0');
  const minutes = String(italianTime.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const emptyBinOut = {
  bin_number: '',
  kg_uscita: '',
  binInfo: null,
  status: null, // null, 'checking', 'ok', 'error', 'notfound'
  sposta_rimanenti: false,
  nuovo_bin_rimanenti: '',
  nuova_doccia_rimanenti: ''
};

const OperatoreOut = () => {
  const [tipoOperazione, setTipoOperazione] = useState('uscita_parziale');
  const [bins, setBins] = useState([{ ...emptyBinOut }]);
  const [spostamentoData, setSpostamentoData] = useState({
    bin_number: '',
    binInfo: null,
    status: null,
    nuovo_bin_number: '',
    nuova_doccia_number: ''
  });
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(getItalianDateTime());
  const [note, setNote] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(getItalianDateTime());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const checkBin = useCallback(async (binNumber, callback) => {
    if (!binNumber || binNumber.length < 1) {
      callback({ status: null, binInfo: null });
      return;
    }

    callback({ status: 'checking' });
    try {
      const response = await axios.get(`${API}/bins/check/${binNumber}`);
      const data = response.data;
      if (data.exists && data.kg_attuali > 0) {
        callback({ status: 'ok', binInfo: data });
      } else {
        callback({ status: 'notfound', binInfo: null });
      }
    } catch (error) {
      callback({ status: 'notfound', binInfo: null });
    }
  }, []);

  const updateBin = (index, updates) => {
    setBins(prev => prev.map((b, i) => i === index ? { ...b, ...updates } : b));
  };

  const addBin = () => {
    setBins(prev => [...prev, { ...emptyBinOut }]);
  };

  const removeBin = (index) => {
    if (bins.length > 1) {
      setBins(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleBinChange = (index, field, value) => {
    updateBin(index, { [field]: value });
    if (field === 'bin_number') {
      setTimeout(() => {
        checkBin(value, (result) => updateBin(index, result));
      }, 500);
    }
  };

  const handleSpostamentoChange = (field, value) => {
    setSpostamentoData(prev => ({ ...prev, [field]: value }));
    if (field === 'bin_number') {
      setTimeout(() => {
        checkBin(value, (result) => setSpostamentoData(prev => ({ ...prev, ...result })));
      }, 500);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (tipoOperazione === 'spostamento') {
        // Spostamento bin e/o doccia
        if (!spostamentoData.binInfo || (!spostamentoData.nuovo_bin_number && !spostamentoData.nuova_doccia_number)) {
          toast.error('Seleziona bin valido e specifica nuovo bin o nuova doccia');
          setLoading(false);
          return;
        }

        const payload = {
          tipo_operazione: 'spostamento',
          bin_number: spostamentoData.bin_number,
          doccia_number: spostamentoData.binInfo.doccia_number,
          kg_movimento: spostamentoData.binInfo.kg_attuali,
          note
        };

        // Aggiungi nuovo bin se specificato
        if (spostamentoData.nuovo_bin_number) {
          payload.nuovo_bin_number = spostamentoData.nuovo_bin_number;
        }

        // Aggiungi nuova doccia se specificata
        if (spostamentoData.nuova_doccia_number) {
          payload.nuova_doccia_number = parseInt(spostamentoData.nuova_doccia_number);
        }

        await axios.post(`${API}/richieste-importazione`, payload);

        toast.success('Richiesta spostamento inviata!');
        setSpostamentoData({ bin_number: '', binInfo: null, status: null, nuova_doccia_number: '', nuovo_bin_number: '' });

      } else {
        // Uscita parziale multi-bin
        const validBins = bins.filter(b => b.binInfo && b.kg_uscita && parseFloat(b.kg_uscita) > 0);
        if (validBins.length === 0) {
          toast.error('Inserisci almeno un bin valido con KG');
          setLoading(false);
          return;
        }

        const promises = validBins.map(bin => {
          const payload = {
            tipo_operazione: 'uscita_parziale',
            bin_number: bin.bin_number,
            doccia_number: bin.binInfo.doccia_number,
            kg_movimento: parseFloat(bin.kg_uscita),
            note
          };

          // Aggiungi dati spostamento rimanenti se attivo
          if (bin.sposta_rimanenti && bin.nuovo_bin_rimanenti && bin.nuova_doccia_rimanenti) {
            payload.sposta_rimanenti = true;
            payload.nuovo_bin_rimanenti = bin.nuovo_bin_rimanenti;
            payload.nuova_doccia_rimanenti = parseInt(bin.nuova_doccia_rimanenti);
          }

          return axios.post(`${API}/richieste-importazione`, payload);
        });

        await Promise.all(promises);
        toast.success(`${validBins.length} richieste uscita inviate!`);
        setBins([{ ...emptyBinOut }]);
      }

      setNote('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante invio');
    } finally {
      setLoading(false);
    }
  };

  const totalKgUscita = bins.reduce((sum, b) => sum + (parseFloat(b.kg_uscita) || 0), 0);
  const validBinsCount = bins.filter(b => b.binInfo && b.kg_uscita && parseFloat(b.kg_uscita) > 0).length;

  return (
    <div className="max-w-lg mx-auto space-y-6" data-testid="operatore-out-page">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-[#EF4444]/20 rounded-2xl mb-4">
          <LogOut className="w-8 h-8 text-[#EF4444]" />
        </div>
        <h2 className="text-2xl font-bold text-white uppercase tracking-tight">Uscita Merce</h2>
        <p className="text-sm text-white/50 mt-2">Registra uscita o spostamento</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Tipo Operazione */}
        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-4">
          <label className="block text-xs font-medium text-white/70 mb-2">Tipo Operazione</label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setTipoOperazione('uscita_parziale')}
              className={`px-3 py-3 text-sm font-bold uppercase rounded-xl border transition-all ${
                tipoOperazione === 'uscita_parziale'
                  ? 'bg-[#EF4444] text-white border-[#EF4444]'
                  : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
              }`}
              data-testid="tipo-uscita-btn"
            >
              Uscita Parziale
            </button>
            <button
              type="button"
              onClick={() => setTipoOperazione('spostamento')}
              className={`px-3 py-3 text-sm font-bold uppercase rounded-xl border transition-all ${
                tipoOperazione === 'spostamento'
                  ? 'bg-[#EF4444] text-white border-[#EF4444]'
                  : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
              }`}
              data-testid="tipo-spostamento-btn"
            >
              Spostamento
            </button>
          </div>
        </div>

        {tipoOperazione === 'uscita_parziale' ? (
          <>
            {/* Multi-Bin Uscita */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-bold text-white">
                  <Layers className="w-4 h-4 text-[#EF4444]" />
                  Bins in uscita ({bins.length})
                </div>
                <button
                  type="button"
                  onClick={addBin}
                  className="flex items-center gap-1 px-3 py-1 bg-[#EF4444]/20 text-[#EF4444] rounded-lg text-xs font-bold hover:bg-[#EF4444]/30 transition-colors"
                  data-testid="add-bin-out-btn"
                >
                  <Plus className="w-3 h-3" />
                  Aggiungi
                </button>
              </div>

              {bins.map((bin, index) => (
                <div 
                  key={index} 
                  className={`bg-white/10 backdrop-blur border rounded-2xl p-4 space-y-3 ${
                    bin.status === 'notfound' ? 'border-[#EF4444]' : 'border-white/20'
                  }`}
                  data-testid={`bin-out-row-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 grid grid-cols-2 gap-3">
                      {/* Bin Number */}
                      <div>
                        <label className="block text-xs font-medium text-white/70 mb-1">Bin</label>
                        <div className="relative">
                          <input
                            type="text"
                            value={bin.bin_number}
                            onChange={(e) => handleBinChange(index, 'bin_number', e.target.value)}
                            className={`w-full px-3 py-2 bg-white/10 border rounded-lg text-white focus:outline-none focus:ring-2 text-sm ${
                              bin.status === 'notfound' ? 'border-[#EF4444] focus:ring-[#EF4444]' : 
                              bin.status === 'ok' ? 'border-[#22C55E] focus:ring-[#22C55E]' : 
                              'border-white/20 focus:ring-[#EF4444]'
                            }`}
                            placeholder="N° Bin"
                            data-testid={`bin-out-number-${index}`}
                          />
                          {bin.status === 'checking' && (
                            <div className="absolute right-2 top-1/2 -translate-y-1/2">
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            </div>
                          )}
                          {bin.status === 'ok' && <CheckCircle className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#22C55E]" />}
                          {bin.status === 'notfound' && <AlertCircle className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#EF4444]" />}
                        </div>
                      </div>

                      {/* KG Uscita */}
                      <div>
                        <label className="block text-xs font-medium text-white/70 mb-1">
                          KG Uscita {bin.binInfo && <span className="text-[#FFE500]">(max {bin.binInfo.kg_attuali})</span>}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          max={bin.binInfo?.kg_attuali}
                          value={bin.kg_uscita}
                          onChange={(e) => handleBinChange(index, 'kg_uscita', e.target.value)}
                          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#EF4444] text-sm"
                          placeholder="0.00"
                          data-testid={`kg-uscita-${index}`}
                        />
                      </div>
                    </div>

                    {bins.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeBin(index)}
                        className="mt-6 p-2 text-[#EF4444] hover:bg-[#EF4444]/20 rounded-lg transition-colors"
                        data-testid={`remove-bin-out-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Info Bin */}
                  {bin.binInfo && (
                    <div className="text-xs text-white/50 bg-white/5 rounded-lg p-2">
                      D{bin.binInfo.doccia_number} | {bin.binInfo.fornitore} | {bin.binInfo.kg_attuali} kg
                    </div>
                  )}

                  {/* Opzione Sposta Rimanenti */}
                  {bin.binInfo && bin.kg_uscita && parseFloat(bin.kg_uscita) < bin.binInfo.kg_attuali && (
                    <div className="p-3 bg-[#FFE500]/10 border border-[#FFE500]/30 rounded-xl space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={bin.sposta_rimanenti}
                          onChange={(e) => handleBinChange(index, 'sposta_rimanenti', e.target.checked)}
                          className="w-4 h-4 rounded"
                        />
                        <span className="text-xs font-medium text-[#FFE500]">
                          Sposta rimanenti ({(bin.binInfo.kg_attuali - parseFloat(bin.kg_uscita)).toFixed(2)} kg)
                        </span>
                      </label>
                      
                      {bin.sposta_rimanenti && (
                        <div className="grid grid-cols-2 gap-2 pt-2">
                          <input
                            type="text"
                            value={bin.nuovo_bin_rimanenti}
                            onChange={(e) => handleBinChange(index, 'nuovo_bin_rimanenti', e.target.value)}
                            className="px-2 py-1 bg-white/10 border border-[#FFE500]/30 rounded-lg text-white text-xs"
                            placeholder="Nuovo Bin"
                          />
                          <select
                            value={bin.nuova_doccia_rimanenti}
                            onChange={(e) => handleBinChange(index, 'nuova_doccia_rimanenti', e.target.value)}
                            className="px-2 py-1 bg-white/10 border border-[#FFE500]/30 rounded-lg text-white text-xs"
                          >
                            <option value="" className="bg-[#0A1628]">Doccia...</option>
                            {Array.from({ length: 12 }, (_, i) => i + 1).map(num => (
                              <option key={num} value={num} className="bg-[#0A1628]">{num}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Riepilogo Uscita */}
            <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-white/70">Bins validi:</span>
                <span className="text-lg font-bold text-[#EF4444]">{validBinsCount}</span>
              </div>
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-white/70">Totale KG Uscita:</span>
                <span className="text-2xl font-bold text-[#EF4444]">{totalKgUscita.toFixed(2)} kg</span>
              </div>
              
              <button
                type="submit"
                disabled={loading || validBinsCount === 0}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#EF4444] text-white rounded-xl font-bold uppercase tracking-wide shadow-lg disabled:opacity-50 hover:bg-[#EF4444]/90 transition-colors"
                data-testid="submit-uscita-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Invia {validBinsCount} Richieste Uscita
                  </>
                )}
              </button>
            </div>
          </>
        ) : (
          /* Spostamento Bin e/o Doccia */
          <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-4 space-y-4">
            <div className="flex items-center gap-2 text-sm font-bold text-[#FFE500]">
              <MoveRight className="w-4 h-4" />
              Spostamento Bin / Doccia
            </div>

            <div>
              <label className="block text-xs font-medium text-white/70 mb-1">Bin da spostare</label>
              <div className="relative">
                <input
                  type="text"
                  value={spostamentoData.bin_number}
                  onChange={(e) => handleSpostamentoChange('bin_number', e.target.value)}
                  className={`w-full px-3 py-2 bg-white/10 border rounded-lg text-white focus:outline-none focus:ring-2 ${
                    spostamentoData.status === 'ok' ? 'border-[#22C55E]' : 'border-white/20'
                  }`}
                  placeholder="N° Bin attuale"
                  data-testid="spostamento-bin-input"
                />
                {spostamentoData.status === 'checking' && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  </div>
                )}
                {spostamentoData.status === 'ok' && <CheckCircle className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#22C55E]" />}
              </div>
            </div>

            {spostamentoData.binInfo && (
              <div className="p-3 bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-xl text-sm">
                <p className="text-white font-medium">{spostamentoData.binInfo.kg_attuali} kg</p>
                <p className="text-white/50 text-xs">Bin: {spostamentoData.bin_number} | Doccia {spostamentoData.binInfo.doccia_number} | {spostamentoData.binInfo.fornitore}</p>
              </div>
            )}

            {/* Nuovo Bin (opzionale) */}
            <div>
              <label className="block text-xs font-medium text-white/70 mb-1">
                Nuovo N° Bin <span className="text-white/40">(lascia vuoto se invariato)</span>
              </label>
              <input
                type="text"
                value={spostamentoData.nuovo_bin_number || ''}
                onChange={(e) => handleSpostamentoChange('nuovo_bin_number', e.target.value)}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                placeholder="Nuovo N° Bin"
                data-testid="spostamento-nuovo-bin-input"
              />
            </div>

            {/* Nuova Doccia (opzionale) */}
            <div>
              <label className="block text-xs font-medium text-white/70 mb-1">
                Nuova Doccia <span className="text-white/40">(lascia vuoto se invariata)</span>
              </label>
              <select
                value={spostamentoData.nuova_doccia_number}
                onChange={(e) => handleSpostamentoChange('nuova_doccia_number', e.target.value)}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                data-testid="spostamento-doccia-select"
              >
                <option value="" className="bg-[#0A1628]">Stessa doccia (invariata)</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(num => (
                  <option key={num} value={num} className="bg-[#0A1628]">Doccia {num}</option>
                ))}
              </select>
            </div>

            {/* Messaggio validazione */}
            {spostamentoData.binInfo && !spostamentoData.nuovo_bin_number && !spostamentoData.nuova_doccia_number && (
              <p className="text-xs text-[#EF4444]">Specifica almeno un nuovo bin o una nuova doccia</p>
            )}

            <button
              type="submit"
              disabled={loading || !spostamentoData.binInfo || (!spostamentoData.nuovo_bin_number && !spostamentoData.nuova_doccia_number)}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#EF4444] text-white rounded-xl font-bold uppercase tracking-wide shadow-lg disabled:opacity-50 hover:bg-[#EF4444]/90 transition-colors"
              data-testid="submit-spostamento-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Invia Richiesta Spostamento
                </>
              )}
            </button>
          </div>
        )}

        {/* Note */}
        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-4">
          <label className="block text-xs font-medium text-white/70 mb-2">Note (opzionale)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#EF4444] text-sm resize-none"
            placeholder="Note aggiuntive..."
            data-testid="note-textarea"
          />
        </div>
      </form>
    </div>
  );
};

export default OperatoreOut;
